import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  UsersIcon,
  CheckIcon,
  BanknotesIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import EnhancedLeadAssignmentModal from './EnhancedLeadAssignmentModal';
import StatusChangeModal from './StatusChangeModal';
import { useFormFields } from '../hooks/useFormFields';
import { leadsApi, authApi } from '../config/api';

const LeadList = ({ leads, onEdit, onDelete, onStatusChange, onScheduleFollowUp, onAssignmentComplete, onAddFollowUp, onViewFollowUpHistory, onBulkAssignment, bulkSelectMode: externalBulkSelectMode, setBulkSelectMode: setExternalBulkSelectMode }) => {
  const navigate = useNavigate();

  const [whatsAppModal, setWhatsAppModal] = useState({
    isOpen: false,
    phone: '',
    lead: null
  });
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const getCompanyId = () => {
    const storedCompanyId = localStorage.getItem('companyId') || localStorage.getItem('selectedCompanyId');
    if (storedCompanyId) return storedCompanyId;

    const token = localStorage.getItem('authToken');
    if (!token) return null;
    try {
      const cleanedToken = token.replace(/['"]/g, '').trim();
      const payload = JSON.parse(atob(cleanedToken.split('.')[1]));
      return payload.companyId || payload.company || payload.id;
    } catch (e) {
      console.error('Error decoding token for companyId:', e);
      return null;
    }
  };

  const defaultWhatsappTemplates = [
    {
      _id: 'default_1',
      title: 'Testing the lead sharing',
      location: 'Kolhapur, Maharashtra, India',
      message: '*Testing the lead sharing*\n\nTesting the lead sharing'
    },
    {
      _id: 'default_2',
      title: 'track',
      location: 'Maharashtra',
      message: '*track*\n\nYour lead is tracking'
    },
    {
      _id: 'default_3',
      title: 'vaibhav',
      location: 'Kolhapur, Maharashtra, India',
      message: '*vaibhav*\n\nvv'
    },
    {
      _id: 'default_4',
      title: 'test',
      location: 'Kolhapur, Maharashtra, India',
      message: '*test*'
    },
    {
      _id: 'default_5',
      title: 'Lead Follow-up',
      location: 'Office',
      message: 'Hello {name}, following up regarding your inquiry. Please let us know a suitable time to connect!'
    }
  ];

  const handleWhatsAppClick = async (phone, lead) => {
    setWhatsAppModal({
      isOpen: true,
      phone: phone,
      lead: lead
    });
    
    if (whatsappTemplates.length === 0) {
      try {
        setTemplatesLoading(true);
        const companyId = getCompanyId();
        
        if (companyId) {
          console.log('🔍 Fetching WhatsApp templates for company:', companyId);
          const response = await authApi.get(`/whatsapp-msg/company/${companyId}`);
          const fetched = response.data || [];
          if (fetched.length > 0) {
            setWhatsappTemplates(fetched);
          } else {
            setWhatsappTemplates(defaultWhatsappTemplates);
          }
        } else {
          setWhatsappTemplates(defaultWhatsappTemplates);
        }
      } catch (error) {
        console.error('❌ Error fetching WhatsApp templates:', error);
        setWhatsappTemplates(defaultWhatsappTemplates);
      } finally {
        setTemplatesLoading(false);
      }
    }
  };

  const resolveMessage = (message, lead) => {
    if (!message) return '';
    let resolved = message;
    resolved = resolved.replace(/{name}/gi, lead?.name || '');
    resolved = resolved.replace(/{leadName}/gi, lead?.name || '');
    resolved = resolved.replace(/{phone}/gi, lead?.phone || '');
    resolved = resolved.replace(/{email}/gi, lead?.email || '');
    resolved = resolved.replace(/{budget}/gi, lead?.budget || '');
    return resolved;
  };

  const sendWhatsAppMessage = (phone, message) => {
    let cleanedPhone = phone.toString().replace(/\D/g, '');
    if (cleanedPhone.length === 10) {
      cleanedPhone = '91' + cleanedPhone;
    }
    const url = `https://api.whatsapp.com/send?phone=${cleanedPhone}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const renderLeadPhone = (phone, lead, isMobile = false) => {
    if (!phone) return null;

    let role = '';
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        role = (userInfo.role || userInfo.userType || '').toLowerCase();
      } else {
        const currentUserStr = localStorage.getItem('currentUser') || localStorage.getItem('userData');
        if (currentUserStr) {
          const currentUser = JSON.parse(currentUserStr);
          role = (currentUser.role || currentUser.userType || '').toLowerCase();
        } else {
          const token = localStorage.getItem('authToken');
          if (token) {
            const cleanedToken = token.replace(/['"]/g, '').trim();
            const payload = JSON.parse(atob(cleanedToken.split('.')[1]));
            role = (payload.role || payload.userType || localStorage.getItem('userRole') || '').toLowerCase();
          }
        }
      }
    } catch (e) {
      console.error('Error getting user role for phone masking:', e);
    }

    const canSee = role === 'company' || 
                   role === 'franchise' || 
                   role === 'manager' || 
                   role === 'admin' || 
                   role === 'superadmin' || 
                   role.includes('manager');

    if (canSee) {
      return (
        <span className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleWhatsAppClick(phone, lead);
            }}
            className="text-emerald-600 hover:text-emerald-700 p-0.5 rounded-full hover:bg-emerald-50 transition-colors flex-shrink-0"
            title="Send WhatsApp Template Message"
          >
            <svg className="w-3.5 h-3.5 fill-current text-green-500 hover:text-green-600" viewBox="0 0 24 24">
              <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.96 9.96 0 001.37 5.028L2 22l5.13-1.346a9.921 9.921 0 004.877 1.28h.005c5.505 0 9.99-4.478 9.99-9.985 0-2.67-1.037-5.18-2.92-7.065C17.198 3.003 14.685 2 12.012 2zm5.776 14.15c-.25.703-1.25 1.295-1.745 1.393-.454.09-1.042.164-3.037-.66-2.55-1.054-4.19-3.655-4.316-3.823-.127-.167-1.026-1.366-1.026-2.607a2.76 2.76 0 01.854-1.964c.264-.265.576-.33.776-.33.197-.002.395-.002.567.006.18.008.419-.068.653.486.242.576.827 2.018.898 2.163.072.146.12.316.023.51-.097.195-.145.316-.29.486-.145.17-.305.378-.435.508-.145.144-.298.302-.128.594.17.29.753 1.24 1.614 2.007.957.854 1.764 1.118 2.014 1.244.25.126.395.106.543-.066.149-.17.638-.744.808-1.013.17-.27.34-.226.574-.14.234.086 1.488.702 1.743.829.255.127.425.19.489.3.064.11.064.636-.186 1.34z"/>
            </svg>
          </button>
          <a 
            href={`tel:${phone}`} 
            onClick={(e) => e.stopPropagation()} 
            className={isMobile ? "flex items-center gap-1 hover:text-blue-600" : "hover:text-blue-600"}
          >
            {isMobile && <PhoneIcon className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0 text-gray-400" />}
            <span>{phone}</span>
          </a>
        </span>
      );
    } else {
      const phoneStr = phone.toString().trim();
      const maskedPhone = phoneStr.length > 4 
        ? '*'.repeat(phoneStr.length - 4) + phoneStr.slice(-4)
        : '******';
      return (
        <span className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleWhatsAppClick(phone, lead);
            }}
            className="text-emerald-600 hover:text-emerald-700 p-0.5 rounded-full hover:bg-emerald-50 transition-colors flex-shrink-0"
            title="Send WhatsApp Template Message"
          >
            <svg className="w-3.5 h-3.5 fill-current text-green-500 hover:text-green-600" viewBox="0 0 24 24">
              <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.96 9.96 0 001.37 5.028L2 22l5.13-1.346a9.921 9.921 0 004.877 1.28h.005c5.505 0 9.99-4.478 9.99-9.985 0-2.67-1.037-5.18-2.92-7.065C17.198 3.003 14.685 2 12.012 2zm5.776 14.15c-.25.703-1.25 1.295-1.745 1.393-.454.09-1.042.164-3.037-.66-2.55-1.054-4.19-3.655-4.316-3.823-.127-.167-1.026-1.366-1.026-2.607a2.76 2.76 0 01.854-1.964c.264-.265.576-.33.776-.33.197-.002.395-.002.567.006.18.008.419-.068.653.486.242.576.827 2.018.898 2.163.072.146.12.316.023.51-.097.195-.145.316-.29.486-.145.17-.305.378-.435.508-.145.144-.298.302-.128.594.17.29.753 1.24 1.614 2.007.957.854 1.764 1.118 2.014 1.244.25.126.395.106.543-.066.149-.17.638-.744.808-1.013.17-.27.34-.226.574-.14.234.086 1.488.702 1.743.829.255.127.425.19.489.3.064.11.064.636-.186 1.34z"/>
            </svg>
          </button>
          <span 
            onClick={(e) => e.stopPropagation()} 
            className={isMobile ? "flex items-center gap-1 text-gray-400 cursor-not-allowed" : "text-gray-400 cursor-not-allowed"} 
            title="Only manager and company can view full contact number"
          >
            {isMobile && <PhoneIcon className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0 text-gray-400" />}
            <span>{maskedPhone}</span>
          </span>
        </span>
      );
    }
  };
  
  // Form fields hook
  const { fields: formFields, loading: fieldsLoading } = useFormFields();
  
  // Assignment states
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  // Use external bulkSelectMode if provided, otherwise use internal state
  const bulkSelectMode = externalBulkSelectMode !== undefined ? externalBulkSelectMode : false;
  const setBulkSelectMode = setExternalBulkSelectMode || (() => {});
  
  // Lead status states
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  
  // Status change modal states
  const [statusChangeModal, setStatusChangeModal] = useState({
    isOpen: false,
    lead: null,
    newStatus: '',
    viewHistoryOnly: false
  });

  // Fetch lead statuses on component mount
  useEffect(() => {
    fetchLeadStatuses();
  }, []);

  const fetchLeadStatuses = async () => {
    try {
      setStatusLoading(true);
      console.log('🔍 Fetching lead statuses from:', leadsApi.defaults.baseURL + '/lead-statuses');
      const response = await leadsApi.get('/lead-statuses');
      console.log('📊 Lead statuses response:', response.data);
      if (response.data.success) {
        setLeadStatuses(response.data.statuses);
        console.log('✅ Lead statuses loaded:', response.data.statuses.length);
        console.log('📋 Status details:', response.data.statuses.map(s => ({
          name: s.name,
          label: s.label,
          color: s.color,
          canTransitionTo: s.canTransitionTo
        })));
      } else {
        console.error('❌ API returned success: false');
      }
    } catch (error) {
      console.error('❌ Error fetching lead statuses:', error);
      console.error('❌ Error details:', error.response?.data);
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusColor = (status) => {
    // Find the status in our dynamic statuses
    const statusObj = leadStatuses.find(s => s.name === status);
    if (statusObj) {
      return `bg-${statusObj.color}-100 text-${statusObj.color}-800`;
    }
    
    // Fallback to hardcoded colors for backward compatibility
    switch (status) {
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-purple-100 text-purple-800';
      case 'qualified': return 'bg-indigo-100 text-indigo-800';
      case 'proposal': return 'bg-blue-100 text-blue-800';
      case 'negotiation': return 'bg-orange-100 text-orange-800';
      case 'closed_won': return 'bg-green-100 text-green-800';
      case 'closed_lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusButtonStyles = (status) => {
    const statusObj = leadStatuses.find(s => s.name === status);
    let colorName = statusObj?.color || 'gray';
    
    if (colorName === 'emerald') colorName = 'green';
    
    switch (status) {
      case 'new': colorName = 'yellow'; break;
      case 'contacted': colorName = 'purple'; break;
      case 'qualified': colorName = 'indigo'; break;
      case 'proposal': colorName = 'blue'; break;
      case 'negotiation': colorName = 'orange'; break;
      case 'closed_won': colorName = 'green'; break;
      case 'closed_lost': colorName = 'red'; break;
    }
    
    const colors = {
      yellow: 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 hover:border-yellow-400 focus:ring-yellow-400',
      purple: 'bg-purple-50 text-purple-800 border-purple-300 hover:bg-purple-100 hover:border-purple-400 focus:ring-purple-400',
      indigo: 'bg-indigo-50 text-indigo-800 border-indigo-300 hover:bg-indigo-100 hover:border-indigo-400 focus:ring-indigo-400',
      blue: 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100 hover:border-blue-400 focus:ring-blue-400',
      orange: 'bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100 hover:border-orange-400 focus:ring-orange-400',
      green: 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100 hover:border-green-400 focus:ring-green-400',
      red: 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100 hover:border-red-400 focus:ring-red-400',
      gray: 'bg-gray-50 text-gray-800 border-gray-300 hover:bg-gray-100 hover:border-gray-400 focus:ring-gray-400'
    };
    
    return colors[colorName] || colors.gray;
  };

  const getOutcomeIcon = (status) => {
    const statusObj = leadStatuses.find(s => s.name === status);
    if (statusObj && statusObj.isFinal) {
      if (statusObj.outcome === 'converted') {
        return { icon: '', text: 'Converted', color: 'text-green-600' };
      } else if (statusObj.outcome === 'rejected') {
        return { icon: '', text: 'Rejected', color: 'text-red-600' };
      } else if (statusObj.outcome === 'on_hold') {
        return { icon: '', text: 'On Hold', color: 'text-yellow-600' };
      }
    }
    return null;
  };

  const getColorStyles = (color) => {
    const colorMap = {
      'blue': { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
      'yellow': { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
      'green': { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
      'emerald': { bg: '#D1FAE5', text: '#047857', border: '#6EE7B7' },
      'purple': { bg: '#E9D5FF', text: '#6B21A8', border: '#C084FC' },
      'orange': { bg: '#FED7AA', text: '#9A3412', border: '#FDBA74' },
      'red': { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
      'amber': { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
      'indigo': { bg: '#E0E7FF', text: '#3730A3', border: '#A5B4FC' },
      'gray': { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' },
      'slate': { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' },
    };
    return colorMap[color] || colorMap['gray'];
  };

  const getAvailableStatusTransitions = (currentStatus) => {
    const currentStatusObj = leadStatuses.find(s => s.name === currentStatus);
    
    if (currentStatusObj && currentStatusObj.canTransitionTo && currentStatusObj.canTransitionTo.length > 0) {
      // Return statuses that current status can transition to, plus current status
      // canTransitionTo contains status names, not ObjectIds
      const availableStatuses = leadStatuses.filter(status => 
        currentStatusObj.canTransitionTo.includes(status.name) || status.name === currentStatus
      );
      return availableStatuses;
    }
    // If no transitions defined, allow all statuses
    return leadStatuses;
  };

  const getSourceLabel = (source) => {
    const sourceMap = {
      'website': 'Website',
      'instagram': 'Instagram',
      'facebook': 'Facebook',
      'whatsapp': 'WhatsApp',
      'linkedin': 'LinkedIn',
      'twitter': 'Twitter',
      'youtube': 'YouTube',
      'referral': 'Referral',
      'email_campaign': 'Email Campaign',
      'phone_call': 'Phone Call',
      'trade_show': 'Trade Show',
      'advertisement': 'Advertisement',
      'other': 'Other'
    };
    return sourceMap[source] || source;
  };

  const getSourceColor = (source) => {
    const colorMap = {
      'website': 'bg-green-100 text-green-800',
      'instagram': 'bg-pink-100 text-pink-800',
      'facebook': 'bg-blue-100 text-blue-800',
      'whatsapp': 'bg-green-100 text-green-800',
      'linkedin': 'bg-indigo-100 text-indigo-800',
      'twitter': 'bg-sky-100 text-sky-800',
      'youtube': 'bg-red-100 text-red-800',
      'referral': 'bg-purple-100 text-purple-800',
      'email_campaign': 'bg-orange-100 text-orange-800',
      'phone_call': 'bg-teal-100 text-teal-800',
      'trade_show': 'bg-gray-100 text-gray-800',
      'advertisement': 'bg-yellow-100 text-yellow-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return colorMap[source] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Assignment functions
  const toggleLeadSelection = (lead) => {
    setSelectedLeads(prev => {
      const isSelected = prev.some(l => l._id === lead._id);
      if (isSelected) {
        return prev.filter(l => l._id !== lead._id);
      } else {
        return [...prev, lead];
      }
    });
  };

  const selectAllLeads = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads([...leads]);
    }
  };

  const handleSingleAssign = (lead) => {
    setSelectedLeads([lead]);
    setShowAssignmentModal(true);
  };

  const handleBulkAssign = () => {
    if (selectedLeads.length === 0) {
      alert('Please select leads to assign');
      return;
    }
    // Use the new bulk assignment modal if available, otherwise fallback to existing modal
    if (onBulkAssignment) {
      onBulkAssignment(selectedLeads);
      setSelectedLeads([]);
      setBulkSelectMode(false);
    } else {
      setShowAssignmentModal(true);
    }
  };

  const handleAssignmentComplete = (result) => {
    setSelectedLeads([]);
    setBulkSelectMode(false);
    if (onAssignmentComplete) {
      onAssignmentComplete(result);
    }
  };

  const isLeadSelected = (lead) => {
    return selectedLeads.some(l => l._id === lead._id);
  };

  if (leads.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
        <div className="text-gray-400 mb-4">
          <BuildingOfficeIcon className="h-16 w-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
        <p className="text-gray-500">Get started by adding your first lead.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-none sm:rounded-lg shadow-sm border-y sm:border sm:border-x-gray-200 border-gray-100 overflow-hidden relative">
      {/* Floating Bulk Assign Button - Shows when leads are selected */}
      {bulkSelectMode && selectedLeads.length > 0 && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-2.5 py-2 md:px-4 md:py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-1.5 md:gap-3 min-w-0">
            <div className="bg-white/20 rounded-full p-1.5 md:p-2 shrink-0">
              <UsersIcon className="h-4 w-4 md:h-5 md:w-5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-xs md:text-sm truncate">{selectedLeads.length} Lead{selectedLeads.length > 1 ? 's' : ''} Selected</p>
              <p className="text-[10px] md:text-xs text-blue-100 hidden sm:block">Ready for bulk assignment</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            <button
              onClick={handleBulkAssign}
              className="bg-white text-blue-600 px-2.5 py-1.5 md:px-4 md:py-2 rounded-lg font-semibold text-xs md:text-sm hover:bg-blue-50 transition-colors flex items-center gap-1 md:gap-2 shadow-md shrink-0"
            >
              <UsersIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span>
                <span className="hidden md:inline">Bulk Assign to Employees</span>
                <span className="md:hidden">Bulk Assign</span>
              </span>
            </button>
            <button
              onClick={() => {
                setSelectedLeads([]);
                setBulkSelectMode(false);
              }}
              className="bg-white/20 hover:bg-white/30 text-white px-2.5 py-1.5 md:px-3 md:py-2 rounded-lg text-xs md:text-sm transition-colors shrink-0"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* ── MOBILE CARD VIEW (hidden on desktop) ───────────────── */}
      <div className="md:hidden divide-y divide-gray-100">
        {leads.map((lead) => (
          <div
            key={lead._id || lead.id}
            onClick={() => navigate(`/lead/${lead._id || lead.id}`)}
            className={`p-2 md:p-3 bg-white hover:bg-slate-50 cursor-pointer transition-colors ${
              isLeadSelected(lead) ? 'border-l-4 border-blue-500 bg-blue-50/70' : ''
            }`}
          >
            {/* Card Header: checkbox (bulk) + title + date */}
            <div className="flex items-start gap-2 mb-1.5 md:mb-2">
              {bulkSelectMode && (
                <input
                  type="checkbox"
                  checked={isLeadSelected(lead)}
                  onChange={() => toggleLeadSelection(lead)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1 h-3.5 w-3.5 md:h-4 md:w-4 text-blue-600 border-gray-300 rounded flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1 flex-wrap">
                  {/* Title/Purpose */}
                  {lead.customFields?.title || lead.title ? (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/lead/${lead._id || lead.id}`);
                      }}
                      className="text-xs md:text-sm font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-1.5 py-0.5 cursor-pointer hover:bg-blue-100 truncate max-w-[55%]"
                    >
                      {(lead.customFields?.title || lead.title || '').replace(/\s*-\s*[a-f0-9]{24}$/i, '')}
                    </span>
                  ) : (
                    <span className="text-[10px] md:text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-1.5 py-0.5">No purpose</span>
                  )}
                  {/* Date */}
                  <span className="text-[9px] md:text-[10px] text-gray-400 whitespace-nowrap">
                    {lead.createdAt ? formatDate(lead.createdAt) : '-'}
                  </span>
                </div>
                {/* Lead Name */}
                <p className="text-xs md:text-sm font-semibold text-gray-900 mt-0.5 md:mt-1 truncate">{lead.name}</p>
              </div>
            </div>

            {/* Contact Row */}
            <div className="flex items-center gap-2.5 mb-1.5 md:mb-2 text-[10px] md:text-xs text-gray-600">
              {lead.email && (
                <a 
                  href={`mailto:${lead.email}`} 
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 truncate max-w-[55%] hover:text-blue-600"
                >
                  <EnvelopeIcon className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0 text-gray-400" />
                  <span className="truncate">{lead.email}</span>
                </a>
              )}
              {lead.phone && renderLeadPhone(lead.phone, lead, true)}
            </div>

            {/* Status + Source + Budget row */}
            <div className="flex items-center gap-1.5 md:gap-2 flex-wrap mb-1.5 md:mb-2">
              {/* Status dropdown */}
              <div 
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-1.5 py-0.5 shadow-2xs"
              >
                <span className="text-[8px] md:text-[9px] text-slate-400 font-bold uppercase tracking-wider">Status:</span>
                <select
                  value={lead.status}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    if (newStatus !== lead.status) {
                      onStatusChange(lead._id || lead.id, newStatus, '');
                    }
                  }}
                  className={`text-[10px] md:text-xs font-bold rounded border px-1.5 py-0.5 cursor-pointer shadow-xs transition-all duration-150 hover:scale-[1.02] focus:outline-none focus:ring-1 focus:ring-offset-1 ${getStatusButtonStyles(lead.status)}`}
                  disabled={statusLoading}
                >
                  {getAvailableStatusTransitions(lead.status).map((s) => (
                    <option key={s._id} value={s.name}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* Source */}
              <span className={`text-[10px] md:text-xs rounded-full px-1.5 py-0.5 font-medium ${getSourceColor(lead.source)}`}>
                {getSourceLabel(lead.source)}
              </span>

              {/* Budget */}
              {lead.budget && (
                <span className="text-[10px] md:text-xs text-gray-600 font-medium flex items-center gap-0.5">
                  <BanknotesIcon className="h-3 w-3 md:h-3.5 md:w-3.5 text-gray-400" />
                  {lead.budget}
                </span>
              )}
            </div>

            {/* Assigned + Actions row */}
            <div className="flex items-center justify-between gap-1.5 md:gap-2 flex-wrap">
              {/* Assigned */}
              <div className="text-[10px] md:text-xs text-gray-500 truncate max-w-[40%]">
                {lead.assignedEmployees?.length > 0 ? (
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 md:w-4 md:h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-bold">
                      {(lead.assignedEmployees[0]?.name || 'E').charAt(0).toUpperCase()}
                    </div>
                    <span className="truncate">{lead.assignedEmployees[0]?.name}</span>
                    {lead.assignedEmployees.length > 1 && <span>+{lead.assignedEmployees.length - 1}</span>}
                  </span>
                ) : lead.assignedEmployeeName ? (
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 md:w-4 md:h-4 bg-green-500 text-white rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-bold">
                      {lead.assignedEmployeeName.charAt(0).toUpperCase()}
                    </div>
                    <span className="truncate">{lead.assignedEmployeeName}</span>
                  </span>
                ) : (
                  <span className="italic text-gray-400 text-[10px] md:text-xs">Unassigned</span>
                )}
              </div>

              {/* Action buttons */}
              <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
                <button
                  onClick={() => handleSingleAssign(lead)}
                  className="flex items-center gap-0.5 md:gap-1 px-1.5 py-0.5 md:px-2 md:py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-[9px] md:text-[10px] font-bold transition-colors"
                >
                  <UserPlusIcon className="h-3 w-3 md:h-3.5 md:w-3.5" />
                  <span>Assign</span>
                </button>
                {onAddFollowUp && (
                  <button
                    onClick={() => onAddFollowUp(lead)}
                    className="flex items-center gap-0.5 md:gap-1 px-1.5 py-0.5 md:px-2 md:py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-[9px] md:text-[10px] font-bold transition-colors"
                  >
                    <PlusIcon className="h-3 w-3 md:h-3.5 md:w-3.5" />
                    <span>Follow</span>
                  </button>
                )}
                <button
                  onClick={() => onEdit(lead)}
                  className="flex items-center gap-0.5 md:gap-1 px-1.5 py-0.5 md:px-2 md:py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-[9px] md:text-[10px] font-bold transition-colors"
                >
                  <PencilIcon className="h-3 w-3 md:h-3.5 md:w-3.5" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => onDelete(lead._id)}
                  className="flex items-center gap-0.5 md:gap-1 px-1.5 py-0.5 md:px-2 md:py-1 bg-red-500 hover:bg-red-600 text-white rounded text-[9px] md:text-[10px] font-bold transition-colors"
                >
                  <TrashIcon className="h-3 w-3 md:h-3.5 md:w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── DESKTOP TABLE VIEW (hidden on mobile) ──────────────── */}
      <div className="lead-table-wrapper hidden md:block">
        <table className="w-full divide-y divide-gray-200 border-collapse table-fixed" style={{ width: '1830px' }}>
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr className="divide-x divide-gray-200">
              {bulkSelectMode && (
                <th className="py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12 sticky left-0 bg-gray-50 z-30 shadow-sm border-r border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onChange={selectAllLeads}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
              )}
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-24">
                Date
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-36">
                Title / Purpose
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-28">
                Lead Name
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-44">
                Contact
              </th>
              
              {/* Dynamic Custom Form Fields Headers */}
              {formFields && formFields.filter(field => field.name !== 'title').map((field) => (
                <th key={field._id} className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-32">
                  {field.label}
                </th>
              ))}
              
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-36">
                Status
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-28">
                Budget
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-24">
                Source
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-32">
                Assigned
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight border-r border-gray-200 w-20">
                Added By
              </th>
              <th className="py-1 text-center text-sm font-medium text-gray-500 uppercase tracking-tight w-80">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {leads.map((lead) => (
              <tr 
                key={lead._id || lead.id} 
                onClick={(e) => {
                  // Don't navigate if clicking on interactive elements
                  const target = e.target;
                  const isInteractive = target.closest('button') || 
                                       target.closest('a') || 
                                       target.closest('input') || 
                                       target.closest('select');
                  
                  if (!isInteractive) {
                    navigate(`/lead/${lead._id || lead.id}`);
                  }
                }}
                className={`hover:bg-blue-50 cursor-pointer transition-colors divide-x divide-gray-200 ${
                  isLeadSelected(lead) ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
              >
                {bulkSelectMode && (
                  <td className="py-1 whitespace-nowrap border-r border-gray-200">
                    <input
                      type="checkbox"
                      checked={isLeadSelected(lead)}
                      onChange={() => toggleLeadSelection(lead)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                )}
                
                {/* Created Date Column - FIRST */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="text-sm text-gray-900">
                    {lead.createdAt ? formatDate(lead.createdAt) : '-'}
                  </div>
                </td>
                
                {/* Title/Purpose Column */}
                <td 
                  className="py-1 text-center border-r border-gray-200 cursor-pointer" 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/lead/${lead._id || lead.id}`);
                  }}
                >
                  <div className="flex justify-center hover:opacity-80 transition-opacity">
                    {lead.customFields?.title || lead.title ? (
                      <div className="text-sm font-semibold text-blue-800 bg-blue-50 px-2 py-0.5 rounded inline-block border border-blue-200 hover:bg-blue-100 hover:underline">
                        {(lead.customFields?.title || lead.title || '').replace(/\s*-\s*[a-f0-9]{24}$/i, '')}
                      </div>
                    ) : (
                      <div className="text-sm text-red-500 bg-red-50 px-2 py-0.5 rounded inline-block border border-red-200">
                        No purpose
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Lead Name Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="text-sm font-medium text-gray-900">
                    {lead.name}
                  </div>
                </td>
                
                {/* Contact Details Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-900 flex items-center">
                      <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                      <a href={`mailto:${lead.email}`} className="hover:text-blue-600 truncate max-w-[150px]">
                        {lead.email}
                      </a>
                    </div>
                    {lead.phone && (
                      <div className="text-sm text-gray-500 flex items-center">
                        <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {renderLeadPhone(lead.phone, lead, false)}
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Dynamic Custom Form Fields Data */}
                {formFields && formFields.filter(field => field.name !== 'title').map((field) => {
                  // Debug log for first lead only
                  if (lead === leads[0]) {
                    console.log(`🔍 Field "${field.name}" (${field.label}):`, lead.customFields?.[field.name]);
                    console.log(`🔍 Full customFields for first lead:`, lead.customFields);
                  }
                  
                  return (
                  <td key={field._id} className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {lead.customFields && lead.customFields[field.name] ? (
                        field.type === 'select' ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {lead.customFields[field.name]}
                          </span>
                        ) : field.type === 'email' ? (
                          <a href={`mailto:${lead.customFields[field.name]}`} className="text-blue-600 hover:text-blue-800">
                            {lead.customFields[field.name]}
                          </a>
                        ) : field.type === 'phone' ? (
                          <a href={`tel:${lead.customFields[field.name]}`} className="text-blue-600 hover:text-blue-800">
                            {lead.customFields[field.name]}
                          </a>
                        ) : field.type === 'textarea' ? (
                          <div className="max-w-32 truncate" title={lead.customFields[field.name]}>
                            {lead.customFields[field.name]}
                          </div>
                        ) : field.type === 'date' ? (
                          <span className="text-gray-900">
                            {new Date(lead.customFields[field.name]).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit', 
                              year: 'numeric'
                            })}
                          </span>
                        ) : (
                          <span className="text-gray-900">
                            {lead.customFields[field.name]}
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400 italic text-xs">
                          No {field.label.toLowerCase().replace(/\s+/g, ' ')}
                        </span>
                      )}
                    </div>
                  </td>
                  );
                })}
                
                {/* Status Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="space-y-2 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex flex-col items-center gap-1.5 p-1 bg-slate-50 border border-slate-200/80 rounded-xl shadow-2xs">
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Change Status:</span>
                        <select
                          value={lead.status}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            if (newStatus !== lead.status) {
                              onStatusChange(lead._id || lead.id, newStatus, '');
                            }
                          }}
                          className={`inline-flex px-3 py-1.5 text-sm font-extrabold rounded-lg border-2 shadow-xs transition-all duration-150 hover:scale-[1.02] focus:outline-none focus:ring-1 focus:ring-offset-1 ${getStatusButtonStyles(lead.status)} cursor-pointer`}
                          disabled={statusLoading}
                          title={`Available transitions from ${lead.status}`}
                        >
                        {statusLoading ? (
                          <option>Loading...</option>
                        ) : (
                          getAvailableStatusTransitions(lead.status).map((status) => (
                            <option key={status._id} value={status.name}>
                              {status.label}
                            </option>
                          ))
                        )}
                        {leadStatuses.length === 0 && !statusLoading && (
                          <option disabled>No statuses available</option>
                        )}
                      </select>
                    </div>
                      
                      {/* Outcome Indicator */}
                      {getOutcomeIcon(lead.status) && (
                        <span 
                          className={`inline-flex items-center gap-1 text-xs font-medium ${getOutcomeIcon(lead.status).color}`}
                          title={getOutcomeIcon(lead.status).text}
                        >
                          {getOutcomeIcon(lead.status).icon}
                        </span>
                      )}
                      
                      {/* Status History Button */}
                      {lead.statusHistory && lead.statusHistory.length > 0 && (
                        <button
                          onClick={() => setStatusChangeModal({
                            isOpen: true,
                            lead: lead,
                            newStatus: lead.status,
                            viewHistoryOnly: true
                          })}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                          title={`View status history (${lead.statusHistory.length} changes)`}
                        >
                          <ClockIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Status Flow Indicator */}
                    {!statusLoading && leadStatuses.length > 0 && (() => {
                      const currentStatusObj = leadStatuses.find(s => s.name === lead.status);
                      const transitions = currentStatusObj?.canTransitionTo || [];
                      
                      if (transitions.length > 0) {
                        return (
                          <div className="flex items-center space-x-1 mt-1">
                            <span className="text-xs text-gray-500 font-medium">Next:</span>
                            <div className="flex flex-wrap gap-1">
                              {transitions.slice(0, 3).map((nextStatusName) => {
                                const nextStatus = leadStatuses.find(s => s.name === nextStatusName);
                                if (!nextStatus) {
                                  return null;
                                }
                                const colors = getColorStyles(nextStatus.color);
                                return (
                                  <span 
                                    key={nextStatus._id}
                                    className="inline-flex px-2 py-0.5 text-xs rounded-md font-medium shadow-sm"
                                    style={{
                                      backgroundColor: colors.bg,
                                      color: colors.text,
                                      border: `1px solid ${colors.border}`
                                    }}
                                    title={`Can transition to: ${nextStatus.label}`}
                                  >
                                    {nextStatus.label}
                                  </span>
                                );
                              })}
                              {transitions.length > 3 && (
                                <span className="text-xs text-gray-500 font-medium" title={`${transitions.length - 3} more transitions available`}>
                                  +{transitions.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div className="text-xs text-gray-400 italic mt-1">
                           
                          </div>
                        );
                      }
                    })()}
                  </div>
                </td>
                
                {/* Value & Budget Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <div className="space-y-1">
                    {lead.budget && (
                      <div className="text-sm text-gray-900 flex items-center justify-center">
                        <BanknotesIcon className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="font-medium">{lead.budget}</span>
                      </div>
                    )}
                    {!lead.budget && (
                      <div className="text-sm text-gray-500 flex items-center justify-center">
                        <BanknotesIcon className="h-4 w-4 mr-1 text-gray-400" />
                        <span>-</span>
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Source Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceColor(lead.source)}`}>
                    {getSourceLabel(lead.source)}
                  </span>
                  {lead.sourceDetails && (
                    <div className="text-xs text-gray-400 mt-1 truncate max-w-32" title={lead.sourceDetails}>
                      {lead.sourceDetails}
                    </div>
                  )}
                </td>
                
                {/* Assigned To Column - Multiple Employees Support with Dropdown */}
                <td className="py-1 text-center border-r border-gray-200">
                  {(lead.assignedEmployees && lead.assignedEmployees.length > 0) ? (
                    <div className="text-sm relative group">
                      <div className="flex flex-wrap gap-1 cursor-pointer" title={lead.assignedByName ? `Assigned by: ${lead.assignedByName}` : ''}>
                        {lead.assignedEmployees.slice(0, 2).map((emp, idx) => (
                          <div key={idx} className="flex items-center bg-blue-50 rounded-full px-2 py-1" title={emp.email || ''}>
                            <div className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-1">
                              {(emp.name || 'E').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs text-gray-700 truncate max-w-[60px]">{emp.name}</span>
                          </div>
                        ))}
                        {lead.assignedEmployees.length > 2 && (
                          <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1 cursor-pointer hover:bg-gray-200">
                            +{lead.assignedEmployees.length - 2} more
                          </span>
                        )}
                      </div>
                      
                      {/* Dropdown on hover - shows all employees */}
                      {lead.assignedEmployees.length > 0 && (
                        <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block">
                          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[220px]">
                            <div className="text-xs font-semibold text-gray-500 mb-2 pb-1 border-b">
                              Assigned Employees ({lead.assignedEmployees.length})
                            </div>
                            {lead.assignedByName && (
                              <div className="text-xs text-gray-600 mb-2 pb-2 border-b bg-purple-50 p-2 rounded">
                                <span className="font-medium">Assigned by:</span> {lead.assignedByName}
                              </div>
                            )}
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {lead.assignedEmployees.map((emp, idx) => (
                                <div key={idx} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                                    {(emp.name || 'E').charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 text-sm truncate">{emp.name}</div>
                                    {emp.email && (
                                      <div className="text-xs text-gray-500 truncate">{emp.email}</div>
                                    )}
                                    {emp.phone && (
                                      <a href={`tel:${emp.phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                        <PhoneIcon className="h-3 w-3" />
                                        {emp.phone}
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : lead.assignedEmployeeName ? (
                    <div className="text-sm" title={lead.assignedByName ? `Assigned by: ${lead.assignedByName}` : ''}>
                      <div className="flex items-center bg-green-50 rounded-full px-2 py-1">
                        <div className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-1">
                          {(lead.assignedEmployeeName || 'E').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-xs text-gray-700 truncate max-w-[80px]">{lead.assignedEmployeeName}</span>
                      </div>
                      {lead.assignedEmployeePhone && (
                        <div className="text-gray-500 text-xs flex items-center mt-1">
                          <PhoneIcon className="h-3 w-3 mr-1 text-gray-400" />
                          <a href={`tel:${lead.assignedEmployeePhone}`} className="hover:text-blue-600">
                            {lead.assignedEmployeePhone}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Unassigned</span>
                  )}
                </td>
                
                {/* Created By Column */}
                <td className="py-1 text-center border-r border-gray-200">
                  {lead.createdByName ? (
                    <div className="text-xs" title={`Added by: ${lead.createdByName}${lead.createdByEmail ? ` (${lead.createdByEmail})` : ''}${lead.createdByRole ? `\nRole: ${lead.createdByRole}` : ''}`}>
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                          {(lead.createdByName || 'U').charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="text-gray-700 text-xs mt-0.5 truncate max-w-[70px] mx-auto font-medium">
                        {lead.createdByName}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400" title="Creator information not available">-</span>
                  )}
                </td>
                
                {/* Actions Column */}
                <td className="py-1 text-center">
                  <div className="flex justify-center space-x-2">
                    {/* Assignment Button */}
                    <button
                      onClick={() => handleSingleAssign(lead)}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                      title="Assign to sales person"
                    >
                      <UserPlusIcon className="h-4 w-4 mr-2" />
                      <span>Assign</span>
                    </button>
                    
                    {onScheduleFollowUp && (
                        <button
                          onClick={() => onScheduleFollowUp(lead)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border-2 border-purple-100 shadow-sm"
                          title="Schedule follow-up"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          <span className="text-xs font-bold">Schedule</span>
                        </button>
                    )}
                    {onAddFollowUp && (
                        <button
                          onClick={() => onAddFollowUp(lead)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors border-2 border-green-100 shadow-sm"
                          title="Add follow-up"
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span className="text-xs font-bold">Add</span>
                        </button>
                    )}
                    {onViewFollowUpHistory && (
                      <button
                        onClick={() => onViewFollowUpHistory(lead)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-purple-100 shadow-sm"
                        title="View follow-up history"
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        <span className="text-xs font-bold">History</span>
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(lead)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-100 shadow-sm"
                      title="Edit lead"
                    >
                      <PencilIcon className="h-4 w-4" />
                      <span className="text-xs font-bold">Edit</span>
                    </button>
                    <button
                      onClick={() => onDelete(lead._id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-100 shadow-sm"
                      title="Delete lead"
                    >
                      <TrashIcon className="h-4 w-4" />
                      <span className="text-xs font-bold">Delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Notes Section - Expandable */}
      <div className="border-t bg-gray-50">
        <div className="px-6 py-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Notes</h4>
          <div className="space-y-2">
            {leads.slice(0, 3).map((lead) => (
              lead.notes && (
                <div key={lead._id} className="text-sm">
                  <span className="font-medium text-gray-700">{lead.name}:</span>
                  <span className="text-gray-600 ml-2">{lead.notes}</span>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
      
      {/* Lead Assignment Modal */}
      <EnhancedLeadAssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        selectedLeads={selectedLeads}
        onAssignmentComplete={handleAssignmentComplete}
      />
      
      {/* Status Change Modal with Remark */}
      <StatusChangeModal
        isOpen={statusChangeModal.isOpen}
        onClose={() => setStatusChangeModal({ isOpen: false, lead: null, newStatus: '', viewHistoryOnly: false })}
        lead={statusChangeModal.lead}
        newStatus={statusChangeModal.newStatus}
        statusHistory={statusChangeModal.lead?.statusHistory || []}
        viewHistoryOnly={statusChangeModal.viewHistoryOnly || false}
        onConfirm={(remark) => {
          if (statusChangeModal.lead && statusChangeModal.newStatus) {
            onStatusChange(statusChangeModal.lead._id, statusChangeModal.newStatus, remark);
          }
          setStatusChangeModal({ isOpen: false, lead: null, newStatus: '', viewHistoryOnly: false });
        }}
      />

      {/* WhatsApp Templates Modal */}
      {whatsAppModal.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[9999] p-4 transition-opacity">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100 flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.73-1.45L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.965C16.588 1.977 14.128 1.988 11.48 1.988c-5.44 0-9.866 4.372-9.87 9.802 0 1.714.453 3.39 1.317 4.868l-.993 3.63 3.733-.968zM17.15 14.3c-.27-.14-1.602-.79-1.85-.88-.25-.09-.43-.14-.61.14-.18.27-.69.88-.85 1.05-.16.17-.32.19-.59.05-2.73-1.37-3.93-2.03-5.5-4.71-.27-.45.27-.42.77-1.41.08-.16.04-.31-.02-.45-.06-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47l-.52-.01c-.18 0-.47.07-.72.34-.25.27-.95.93-.95 2.27s.98 2.62 1.11 2.8c.14.18 1.93 2.94 4.67 4.12.65.28 1.16.45 1.56.57.65.21 1.25.18 1.72.11.53-.08 1.6-.66 1.83-1.29.23-.63.23-1.18.16-1.29-.07-.11-.25-.25-.52-.39z" />
                  </svg>
                  Select Template
                </h3>
                <p className="text-xs text-emerald-50 opacity-90 mt-0.5">
                  To: <span className="font-semibold">{whatsAppModal.lead?.name}</span> ({whatsAppModal.phone})
                </p>
              </div>
              <button
                onClick={() => setWhatsAppModal({ isOpen: false, phone: '', lead: null })}
                className="text-white hover:text-emerald-100 transition-colors focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {templatesLoading ? (
                <div className="flex flex-col items-center justify-center py-8 space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  <span className="text-sm text-gray-500 font-medium">Loading templates...</span>
                </div>
              ) : whatsappTemplates.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h4 className="text-sm font-semibold text-gray-800">No Templates Found</h4>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                    Go to <strong>Management &gt; WhatsApp Msg</strong> to create message templates first.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {whatsappTemplates.map((template) => {
                    const previewBody = resolveMessage(template.message, whatsAppModal.lead);
                    const combinedMessage = `*${template.title}*\n\n${previewBody}`;
                    return (
                      <div
                        key={template._id}
                        onClick={() => {
                          sendWhatsAppMessage(whatsAppModal.phone, combinedMessage);
                          setWhatsAppModal({ isOpen: false, phone: '', lead: null });
                        }}
                        className="bg-white hover:bg-emerald-50/30 border border-gray-200 hover:border-emerald-300 rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-md group relative overflow-hidden text-left"
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-emerald-500 transition-colors"></div>
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="text-sm font-bold text-gray-800 group-hover:text-emerald-700 transition-colors">
                            {template.title}
                          </h4>
                          {template.location && (
                            <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                              📍 {template.location}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 whitespace-pre-wrap break-words bg-gray-50 group-hover:bg-white p-2.5 rounded-lg border border-gray-100 group-hover:border-emerald-100 transition-all font-mono leading-relaxed max-h-32 overflow-y-auto">
                          {combinedMessage}
                        </p>
                        <div className="mt-2 text-right">
                          <span className="text-[10px] font-bold text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end gap-1">
                            Send Message
                            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setWhatsAppModal({ isOpen: false, phone: '', lead: null })}
                className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 transition-colors shadow-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadList;
